import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Select, Table, message } from "antd";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DataCard } from "../../shared/ui/DataCard";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { extractErrorMessage } from "../../shared/api/axios";
import { fetchMotoristas } from "../motoristas/api";
import { assignLocker, fetchAssignments, fetchColletos, unassignLocker } from "./api";

export default function Roteirizador() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [locker, setLocker] = useState("");
  const [motoristaId, setMotoristaId] = useState("");

  const [motoristasQuery, colletosQuery, assignmentsQuery] = useQueries({
    queries: [
      { queryKey: ["motoristas"], queryFn: fetchMotoristas },
      { queryKey: ["colletos"], queryFn: fetchColletos },
      { queryKey: ["assignments"], queryFn: fetchAssignments },
    ],
  });

  const motoristas = motoristasQuery.data || [];
  // Memoizado para que useEffect([colletos]) só dispare quando data realmente mudar
  // (sem useMemo, `data || []` cria array novo a cada render).
  const colletos = useMemo(() => colletosQuery.data || [], [colletosQuery.data]);
  const assignments = assignmentsQuery.data || [];

  useEffect(() => {
    if (colletos.length === 0) {
      setLocker("");
      return;
    }
    setLocker((current) =>
      current && colletos.some((item) => item.name === current) ? current : colletos[0].name,
    );
  }, [colletos]);

  const validLockers = colletos.map((item) => item.name);
  const visibleAssignments = assignments.filter((item) => validLockers.includes(item.locker));

  const assignMutation = useMutation({
    mutationFn: assignLocker,
    onSuccess: () => {
      message.success("Locker atribuído");
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
    onError: (err) => message.error(extractErrorMessage(err, "Erro ao atribuir locker")),
  });

  const unassignMutation = useMutation({
    mutationFn: (assignmentId) => unassignLocker(assignmentId),
    onSuccess: () => {
      message.success("Motorista desatribuído");
      setMotoristaId("");
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
    onError: (err) => message.error(extractErrorMessage(err, "Erro ao desatribuir locker")),
  });

  const handleAssign = () => {
    if (!locker || !motoristaId) {
      message.warning("Selecione um motorista");
      return;
    }
    assignMutation.mutate({ locker, motorista_id: motoristaId });
  };

  const handleUnassign = (assignment) => {
    if (!assignment?.id) {
      message.warning("Selecione um locker atribuído");
      return;
    }
    unassignMutation.mutate(assignment.id);
  };

  const columns = [
    { title: "Locker", dataIndex: "locker", key: "locker" },
    {
      title: "Motorista",
      dataIndex: "motorista",
      key: "motorista",
      render: (value) => value || "-",
    },
    { title: "E-mail", dataIndex: "email", key: "email", render: (value) => value || "-" },
    {
      title: "Ações",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Popconfirm
          title="Desatribuir motorista?"
          description={`O locker ${record.locker} ficará sem motorista.`}
          okText="Desatribuir"
          cancelText="Cancelar"
          onConfirm={() => handleUnassign(record)}
        >
          <Button danger disabled={!record.motorista}>
            Desatribuir
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="te-page-wrap te-stack">
      <PageHeader
        title="Roteirizador"
        subtitle="Organize os lockers por motorista e mantenha a operação distribuída de forma clara."
        metricLabel="Atribuições ativas"
        metricValue={visibleAssignments.length}
      />

      <Card className="te-card">
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ margin: 0, color: "var(--te-primary)" }}>Nova atribuição</h3>
          <p className="te-kicker">Selecione o locker e o motorista responsável por ele.</p>
        </div>

        <div className="te-split-grid te-split-grid--two" style={{ gap: 16 }}>
          <div>
            <label className="te-label">Locker</label>
            <Select
              style={{ width: "100%" }}
              size="large"
              placeholder="Selecione o locker"
              value={locker || undefined}
              onChange={(value) => setLocker(value)}
              options={colletos.map((item) => ({ value: item.name, label: item.name }))}
            />
          </div>

          <div>
            <label className="te-label">Motorista</label>
            <Select
              style={{ width: "100%" }}
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder="Selecione o motorista"
              value={motoristaId || undefined}
              onChange={(value) => setMotoristaId(value)}
              allowClear
              options={motoristas.map((m) => ({
                value: m.id,
                label: `${m.nome} (${m.email})`,
              }))}
            />
          </div>
        </div>

        <div className="te-form-action" style={{ marginTop: 6 }}>
          <Button
            type="primary"
            onClick={handleAssign}
            disabled={!locker}
            loading={assignMutation.isPending}
          >
            Atribuir
          </Button>
        </div>
      </Card>

      <Card className="te-card">
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: "var(--te-primary)" }}>Lista de atribuições</h3>
          <p className="te-kicker">Veja rapidamente quem está responsável por cada locker.</p>
        </div>
        {isMobile ? (
          <div className="te-stack">
            {visibleAssignments.map((assignment) => (
              <DataCard
                key={assignment.id}
                title={assignment.locker || "-"}
                rows={[
                  { label: "Motorista", value: assignment.motorista || "-" },
                  { label: "E-mail", value: assignment.email || "-" },
                ]}
                actions={
                  <Popconfirm
                    title="Desatribuir motorista?"
                    description={`O locker ${assignment.locker} ficará sem motorista.`}
                    okText="Desatribuir"
                    cancelText="Cancelar"
                    onConfirm={() => handleUnassign(assignment)}
                  >
                    <Button danger size="small" disabled={!assignment.motorista}>
                      Desatribuir
                    </Button>
                  </Popconfirm>
                }
              />
            ))}
          </div>
        ) : (
          <Table
            dataSource={visibleAssignments}
            columns={columns}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 8, showSizeChanger: false }}
            loading={assignmentsQuery.isLoading}
          />
        )}
      </Card>
    </div>
  );
}
